/* eslint-disable react/react-in-jsx-scope -- Unaware of jsxImportSource */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { formatDistance, subMinutes } from "date-fns";
import { useAppContext } from "../../../hooks/useAppContext";
import { useAppSelector } from "../../../../states/hooks";
import { selectCurrentRound } from "../../../../states/currentRoundState";
import { useDateFnsLocale } from "../../../hooks/useDateFnsLocale";
import { TableContainer as CustomTableContainer } from "../components/TableContainer";
import { LoadingData } from "../components/LoadingData";
import { poolNodeUrl } from "../../../../enviroments";
import { formatAmount } from "../../../utils/functions/formatAmount";
import { asRSAddress } from "../../../utils/functions/accountAddress";
import { viewAccountInExplorer, viewBlockInExplorer } from "../../../utils/explorer";
import { columns } from "./columns";

import useSWR from "swr";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Tooltip from "@mui/material/Tooltip";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import WidgetsIcon from "@mui/icons-material/Widgets";
import PaidIcon from "@mui/icons-material/Paid";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const PoolWonBlocks = () => {
    const { t } = useTranslation();
    const { Fetcher } = useAppContext();
    const { networkInfo } = useAppSelector(selectCurrentRound);
    const locale = useDateFnsLocale();
    const theme = useTheme();
    const isCardLayout = true;

    const defaultSWRSettings = {
        refreshInterval: 60000,
        dedupingInterval: 50000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    };

    const { data, isValidating, error } = useSWR(
        poolNodeUrl + "/api/getWonBlocks",
        Fetcher,
        defaultSWRSettings
    );

    if (isValidating && !data)
        return (
            <CustomTableContainer>
                <LoadingData />
            </CustomTableContainer>
        );

    const blocks = data.wonBlocks || [];
    const processingIndicator = (
        <Tooltip title={`${t("processing")}`} arrow>
            <CircularProgress size={22} />
        </Tooltip>
    );

    const blockAge = (height: number) => {
        if (!networkInfo.blockHeight) return "";
        const differenceInBlocks = networkInfo.blockHeight - height;
        const blockSpecificDate = subMinutes(new Date(), differenceInBlocks * 4);
        return formatDistance(blockSpecificDate, new Date(), { addSuffix: true, locale });
    };

    if ((!isValidating && !blocks.length) || error)
        return (
            <CustomTableContainer>
                <Typography variant="h6" align="center">
                    {t("noBlocksWon") + " 💎"}
                </Typography>
            </CustomTableContainer>
        );

    if (isCardLayout) {
        return (
            <div className="smBlocksCardsPanel">
                <div className="smCardsInfoToolbar">
                    <div>
                        <span><InfoOutlinedIcon /> {t("blocksWon")}</span>
                        <strong>Gefundene Pool-Blöcke als kompakte Reward-Cards mit Explorer-Link.</strong>
                    </div>
                    <div className="smCardsLegend">
                        <Chip icon={<WidgetsIcon />} label="Block" size="small" />
                        <Chip icon={<PaidIcon />} label="Reward" size="small" />
                        <Chip icon={<AccountCircleIcon />} label="Miner" size="small" />
                    </div>
                </div>
                <Grid container spacing={2} className="smBlocksCardGrid">
                {blocks.map((block: any) => {
                    const miner = block.generator;
                    const minerLabel = block.name || asRSAddress(miner);
                    const reward = block.reward === "Processing..." ? processingIndicator : block.reward;
                    const poolReward = block.poolShare === "Processing..." ? processingIndicator : block.poolShare;
                    const age = blockAge(block.height);

                    return (
                        <Grid item xs={12} sm={6} md={6} xl={4} key={block.height}>
                            <Paper className="smBlockWonCard smBlockWonCardWide" elevation={0}>
                                <div className="smBlockWonTop">
                                    <Chip icon={<WidgetsIcon />} label={formatAmount(block.height)} className="smRankChip" onClick={() => viewBlockInExplorer(block.height)} />
                                    {age && <span>{age}</span>}
                                </div>
                                <Typography component="button" className="smMinerCardName" onClick={() => viewAccountInExplorer(miner)}>
                                    {minerLabel}
                                </Typography>
                                <div className="smBlockRewardRows" aria-label="Block Reward Übersicht">
                                    <div className="smBlockRewardRow">
                                        <span><PaidIcon /> {t("rewardAndFees") || "Block-Reward"}</span>
                                        <strong>{reward}</strong>
                                    </div>
                                    <div className="smBlockRewardRow">
                                        <span><AccountCircleIcon /> {t("poolReward") || "Pool-Reward"}</span>
                                        <strong>{poolReward}</strong>
                                    </div>
                                </div>
                                <Button className="smCardDetailsButton" onClick={() => viewBlockInExplorer(block.height)}>Explorer</Button>
                            </Paper>
                        </Grid>
                    );
                })}
                </Grid>
            </div>
        );
    }

    return (
        <Paper elevation={0} className="smPremiumTablePanel">
            <TableContainer sx={{ maxHeight: { xs: undefined, lg: 650 } }} className="smPremiumTableScroll">
                <Table stickyHeader aria-label="blocks won table" className="smPremiumTable">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth, borderRight: 1, borderColor: "divider" }} css={css`:last-child { border-right: 0 !important; }`}>
                                    {t(column.id)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {blocks.map((block: any) => (
                            <TableRow hover tabIndex={-1} key={block.height} className="smPremiumTableRow">
                                {columns.map((column) => {
                                    let cellContent: any = "-";
                                    switch (column.id) {
                                        case "blockHeight":
                                            const age = blockAge(block.height);
                                            cellContent = (
                                                <Grid container direction="column" alignItems="center" justifyContent="center">
                                                    <Typography className="smMinerLink" onClick={() => viewBlockInExplorer(block.height)}>{formatAmount(block.height)}</Typography>
                                                    {age && <Typography color="textSecondary" variant="subtitle2">{age}</Typography>}
                                                </Grid>
                                            );
                                            break;
                                        case "rewardAndFees": cellContent = block.reward === "Processing..." ? processingIndicator : block.reward; break;
                                        case "miner":
                                            const miner = block.generator;
                                            cellContent = <Typography className="smMinerLink" onClick={() => viewAccountInExplorer(miner)}>{block.name || asRSAddress(miner)}</Typography>;
                                            break;
                                        case "poolReward": cellContent = block.poolShare === "Processing..." ? processingIndicator : block.poolShare; break;
                                        default: return null;
                                    }
                                    return (
                                        <TableCell key={column.id} align={column.align} sx={{ borderRight: 1, borderColor: "divider", fontWeight: 800, fontSize: 14 }} css={css`:last-child { border-right: 0 !important; }`}>
                                            {cellContent}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};
